import { PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Col, Icon, Input, Row, Tabs, Tooltip, message } from 'antd';
import { Link } from 'react-router-dom';
import { mappingRoles } from '~constants';
import { landlordContactType, landlordDetailTabs } from '~constants/landlord';
import { fetch } from '~helpers/graphql';
import { contactInfoValidator } from '~helpers/property-edit';
import enhanceForm from '~hocs/enhance-form';
import AccountOwnerSearch from '~modules/account-owner-search';
import PropertyContactModal from '~pages/dashboard/properties/reference-and-contact/contact-editor';
import NewContactItem from '~pages/dashboard/properties/reference-and-contact/newContactItem';
import * as queries from '~settings/queries';
import generatePath from '~settings/routing';
import { onlyViewPropertyRole } from '~client/helpers/auth';

const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!%$&'()*+,;=.]+$/;
const includeRoleArr = [mappingRoles.SUPPLY, mappingRoles.REGIONAL_SUPPLY_HEAD, mappingRoles.ADMIN];
const editRoleArr = [
  mappingRoles.ADMIN,
  mappingRoles.SUPPLY,
  mappingRoles.CONTENT_MANAGER,
  mappingRoles.REGIONAL_SUPPLY_HEAD,
];

@enhanceForm()
export default class Content extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isShowEditContactModal: false,
      modalType: 'create',
      currentContactType: landlordContactType.PROPERTY,
      currentContact: null,
    };
  }
  componentDidMount() {
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  contactValidator = (rule, value, callback) => {
    const { isAllValid } = contactInfoValidator(value);
    if (isAllValid) {
      callback();
    }
    callback('error');
  };

  urlValidator = (rule, value, callback) => {
    if (value && !urlRegex.test(value)) {
      callback('error');
    } else {
      callback();
    }
  };

  validatUrl = url => {
    if (urlRegex.test(url)) {
      return true;
    }
    return false;
  };

  handleCheckButtonClick = value => {
    if (!value.startsWith('https://') && !value.startsWith('http://')) {
      window.open(`http://${value}`);
      return;
    }

    window.open(value);
  };

  formatContacts = contacts => {
    const formattedContacts = contacts
      .map(contact => {
        const { contactName, contactPhone, contactEmail, occupation, id, type } = contact;

        const bindContact = {
          ...(type && { type }),
          contactName: contactName || null,
          contactPhone: contactPhone || null,
          contactEmail: contactEmail || null,
          occupation: occupation || null,
        };

        if (id) {
          bindContact.id = id;
        }

        return bindContact;
      })
      .filter(contact => {
        const { contactName, contactPhone, contactEmail, occupation } = contact;
        return contactName || contactPhone || contactEmail || occupation;
      });

    return formattedContacts;
  };

  onSubmit = (successCallback = () => {}) => {
    const { t, property } = this.props;

    this.props.form.validateFieldsAndScroll(err => {
      if (!err) {
        const { landlordPhotograpyUrl, roomMatrixUrl, url, accountManager } =
          this.props.form.getFieldsValue();
        const requests = [];
        if (url || roomMatrixUrl || landlordPhotograpyUrl || accountManager) {
          requests.push(
            fetch(
              queries.updatePropertyInternalFields({
                id: property.id,
                url: url || null,
                roomMatrixUrl: roomMatrixUrl || null,
                landlordPhotograpyUrl: landlordPhotograpyUrl || null,
                accountManager: accountManager || null,
              }),
            ),
          );
        }

        this.props.onChangeIsFetching(true);
        Promise.all(requests)
          .then(() => {
            successCallback();
            message.success(t('cms.properties.edit.toast.save_success'));
          })
          .catch(() => {
            message.error(t('cms.properties.edit.toast.error'));
          })
          .finally(() => {
            this.props.onChangeIsFetching(false);
          });
      }
    });
  };

  handleDeletePropertyContact = contacts => {
    const { property, t, getLatestReferenceAndContact } = this.props;
    fetch(
      queries.bindPropertyContacts({
        propertyId: property.id,
        propertyContacts: this.formatContacts(contacts),
      }),
    )
      .then(() => {
        getLatestReferenceAndContact();
        message.success(t('cms.properties.edit.toast.save_success'));
      })
      .catch(() => {
        message.error(t('cms.properties.edit.toast.error'));
      });
  };

  handleDeleteBookingTeamContact = bookingTeamContacts => {
    const { property, t, getLatestReferenceAndContact } = this.props;
    fetch(
      queries.bindBookingTeamContacts({
        propertyId: property.id,
        bookingTeamContacts: this.formatContacts(bookingTeamContacts),
      }),
    )
      .then(() => {
        getLatestReferenceAndContact();
        message.success(t('cms.properties.edit.toast.save_success'));
      })
      .catch(() => {
        message.error(t('cms.properties.edit.toast.error'));
      });
  };

  handleCloseEditContactModal = () => {
    this.setState({
      isShowEditContactModal: false,
      modalType: 'create',
      currentContactType: landlordContactType.PROPERTY,
      currentContact: null,
    });
  };

  getPropertyContactWithType = type => {
    const { property } = this.props;
    switch (type) {
      case landlordContactType.PROPERTY:
        return property.propertyContacts.map(contact => ({
          ...contact,
          type: landlordContactType.PROPERTY,
        }));
      case landlordContactType.BOOKING_TEAM:
        return property.bookingTeamContacts.map(contact => ({
          ...contact,
          type: landlordContactType.BOOKING_TEAM,
        }));
      case landlordContactType.FINANCE_TEAM:
        return property.financeTeamContacts.map(contact => ({
          ...contact,
          type: landlordContactType.FINANCE_TEAM,
        }));
      default:
        return [];
    }
  };

  currentSelectedContacts = () => {
    const { property } = this.props;
    const { currentContactType, currentContact } = this.state;
    switch (currentContactType) {
      case landlordContactType.PROPERTY:
        // Property Contacts and finance team contacts be seen as a whole.
        // So when you CURD it, you need carry the other contacts.
        // if you feel confuse, please ask backend engineer :)
        return this.getPropertyContactWithType(landlordContactType.PROPERTY)
          .filter(contact => contact.id !== currentContact?.id)
          .concat(this.getPropertyContactWithType(landlordContactType.FINANCE_TEAM));
      case landlordContactType.BOOKING_TEAM:
        return property.bookingTeamContacts.filter(contact => contact.id !== currentContact?.id);
      case landlordContactType.FINANCE_TEAM:
        return this.getPropertyContactWithType(landlordContactType.FINANCE_TEAM)
          .filter(contact => contact.id !== currentContact?.id)
          .concat(this.getPropertyContactWithType(landlordContactType.PROPERTY));
      default:
        return [];
    }
  };

  render() {
    const { t, property, currentRoleSlug } = this.props;
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const { modalType, currentContactType, currentContact } = this.state;
    return (
      <Form layout="vertical" className="reference-and-contact__content">
        <div className="reference-and-contact__section">
          <div className="reference-and-contact__title">
            {t('cms.properties.edit.others.reference_informaton')}
          </div>
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item label={t('cms.properties.edit.others.original_website')}>
                {getFieldDecorator('url', {
                  initialValue: property.url,
                  validateTrigger: ['onBlur'],
                  rules: [
                    {
                      validator: this.urlValidator,
                      message: this.props.t(
                        'cms.properties.edit.reference_and_contact.invalid_hint',
                      ),
                    },
                  ],
                })(
                  <Input
                    className="reference-and-contact__url-input"
                    autoComplete="off"
                    placeholder={t('cms.properties.edit.others.input_link')}
                    suffix={
                      <Button
                        className={classNames('reference-and-contact__check-btn', {
                          'reference-and-contact__check-btn--active': this.validatUrl(
                            getFieldValue('url'),
                          ),
                        })}
                        onClick={() => {
                          this.handleCheckButtonClick(getFieldValue('url'));
                        }}
                        disabled={!this.validatUrl(getFieldValue('url'))}
                      >
                        {t('cms.properties.edit.address.button_check')}
                      </Button>
                    }
                  />,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('cms.properties.edit.others.landlord_photograpy_url')}>
                {getFieldDecorator('landlordPhotograpyUrl', {
                  initialValue: property.landlordPhotograpyUrl,
                  validateTrigger: ['onBlur'],
                  rules: [
                    {
                      validator: this.urlValidator,
                      message: this.props.t(
                        'cms.properties.edit.reference_and_contact.invalid_hint',
                      ),
                    },
                  ],
                })(
                  <Input
                    placeholder={t('cms.properties.edit.others.input_link')}
                    className="reference-and-contact__url-input"
                    autoComplete="off"
                    suffix={
                      <Button
                        className={classNames('reference-and-contact__check-btn', {
                          'reference-and-contact__check-btn--active': this.validatUrl(
                            getFieldValue('landlordPhotograpyUrl'),
                          ),
                        })}
                        onClick={() => {
                          this.handleCheckButtonClick(getFieldValue('landlordPhotograpyUrl'));
                        }}
                        disabled={!this.validatUrl(getFieldValue('landlordPhotograpyUrl'))}
                      >
                        {t('cms.properties.edit.address.button_check')}
                      </Button>
                    }
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item label={t('cms.properties.edit.others.room_matrix_url')}>
                {getFieldDecorator('roomMatrixUrl', {
                  initialValue: property.roomMatrixUrl,
                  validateTrigger: ['onBlur'],
                  rules: [
                    {
                      validator: this.urlValidator,
                      message: this.props.t(
                        'cms.properties.edit.reference_and_contact.invalid_hint',
                      ),
                    },
                  ],
                })(
                  <Input
                    placeholder={t('cms.properties.edit.others.input_link')}
                    className="reference-and-contact__url-input"
                    autoComplete="off"
                    suffix={
                      <Button
                        className={classNames('reference-and-contact__check-btn', {
                          'reference-and-contact__check-btn--active': this.validatUrl(
                            getFieldValue('roomMatrixUrl'),
                          ),
                        })}
                        onClick={() => {
                          this.handleCheckButtonClick(getFieldValue('roomMatrixUrl'));
                        }}
                        disabled={!this.validatUrl(getFieldValue('roomMatrixUrl'))}
                      >
                        {t('cms.properties.edit.address.button_check')}
                      </Button>
                    }
                  />,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <AccountOwnerSearch
                labelPlaceholder={this.props.t('cms.properties.edit.others.account_owner')}
                inputPlaceholder={this.props.t('cms.properties.edit.others.account_owner_hint')}
                disabledClassName={
                  !editRoleArr.includes(currentRoleSlug)
                    ? 'reference-and-contact__disabled-label'
                    : ''
                }
                includeRoleArr={includeRoleArr}
                accountOwners={this.props.accountOwners}
                isDisabled={!editRoleArr.includes(currentRoleSlug)}
                form={this.props.form}
                t={this.props.t}
                isFetching={this.props.isGettingAccountOwners}
                accountManager={this.props.property.accountManager}
                isNeedInit
                getAccountOwners={this.props.getAccountOwners}
              />
            </Col>
          </Row>
        </div>
        <div className="reference-and-contact__section">
          <div className="reference-and-contact__title">
            {t('cms.properties.edit.others.contact_information')}
          </div>
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane
              tab={
                <Tooltip title="Servicing supply team to assist the booking team to follow up on the booking applications">
                  {t('cms.reference_and_contact.property_contact.tab')}
                </Tooltip>
              }
              key="1"
            >
              <Choose>
                <When
                  condition={
                    !property.propertyContacts?.length && !property.landlordPropertyContacts?.length
                  }
                >
                  <div className="reference-and-contact__section__blank">
                    <p>
                      {t('cms.reference_and_contact.property_contact_section.blank.tip')}.
                      <span
                        onClick={() => {
                          if (!onlyViewPropertyRole()) {
                            this.setState({
                              isShowEditContactModal: true,
                              modalType: 'create',
                              currentContactType: landlordContactType.PROPERTY,
                              currentContact: null,
                            });
                          }
                        }}
                      >
                        <PlusOutlined className="reference-and-contact__add-icon" />
                        {t(`cms.reference_and_contact.add_property_contact.button`)}
                      </span>
                    </p>
                  </div>
                </When>
                <When condition={onlyViewPropertyRole()}>
                  <></>
                </When>
                <Otherwise>
                  <div
                    className="reference-and-contact__add-contact"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!onlyViewPropertyRole()) {
                        this.setState({
                          isShowEditContactModal: true,
                          modalType: 'create',
                          currentContactType: landlordContactType.PROPERTY,
                          currentContact: null,
                        });
                      }
                    }}
                  >
                    <PlusOutlined className="reference-and-contact__add-icon" />
                    {t(`cms.reference_and_contact.add_property_contact.button`)}
                  </div>
                </Otherwise>
              </Choose>

              <If condition={property.propertyContacts.length}>
                <div className="reference-and-contact__contact-section">
                  <h1 className="reference-and-contact__contact-section__title">
                    {t('cms.reference_and_contact.contact_section.level.property')}
                  </h1>
                  <div className="reference-and-contact__contact-section__list">
                    {property.propertyContacts.map((contact, index) => (
                      <NewContactItem
                        onlyRead={false}
                        contact={contact}
                        index={index + 1}
                        contactApplyLevel={landlordContactType.PROPERTY}
                        contactType={landlordContactType.PROPERTY}
                        handleEdit={() => {
                          this.setState({
                            isShowEditContactModal: true,
                            modalType: 'edit',
                            currentContactType: landlordContactType.PROPERTY,
                            currentContact: contact,
                          });
                        }}
                        handleDelete={() => {
                          this.handleDeletePropertyContact(
                            this.getPropertyContactWithType(landlordContactType.PROPERTY)
                              .filter(c => c.id !== contact.id)
                              .concat(
                                this.getPropertyContactWithType(landlordContactType.FINANCE_TEAM),
                              ),
                          );
                        }}
                      />
                    ))}
                  </div>
                </div>
              </If>
              <If condition={property.landlordPropertyContacts.length}>
                <div className="reference-and-contact__contact-section">
                  <h1 className="reference-and-contact__contact-section__title">
                    {t('cms.reference_and_contact.contact_section.level.landlord')}
                  </h1>
                  <div className="reference-and-contact__contact-section__list">
                    {property.landlordPropertyContacts.map((contact, index) => (
                      <NewContactItem
                        onlyRead
                        contact={contact}
                        index={(property.propertyContacts?.length || 0) + index + 1}
                        contactApplyLevel={landlordContactType.LANDLORD}
                        contactType={landlordContactType.PROPERTY}
                      />
                    ))}
                  </div>
                  <p className="reference-and-contact__contact-section__tip">
                    <Icon type="info-circle" />
                    {t('cms.reference_and_contact.cant_edit.tip')}.
                    <Link
                      to={generatePath(
                        'landlord',
                        { landlordSlug: property?.landlord?.slug },
                        {
                          tab: landlordDetailTabs.CONTACT,
                          contactType: landlordContactType.PROPERTY,
                        },
                      )}
                    >
                      <span className="reference-and-contact__link">
                        {t('cms.reference_and_contact.cant_edit.link')}
                      </span>
                    </Link>
                  </p>
                </div>
              </If>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <Tooltip title="Servicing booking team to check inventory, process booking applications and resolve after-sales issues, etc.">
                  {t('cms.reference_and_contact.booking_team_contact.tab')}
                </Tooltip>
              }
              key="2"
            >
              <Choose>
                <When
                  condition={
                    !property.bookingTeamContacts?.length &&
                    !property.landlordBookingTeamContacts?.length
                  }
                >
                  <div className="reference-and-contact__section__blank">
                    <p>
                      {t('cms.reference_and_contact.booking_team_contact_section.blank.tip')}.
                      <span
                        onClick={() => {
                          if (!onlyViewPropertyRole()) {
                            this.setState({
                              isShowEditContactModal: true,
                              modalType: 'create',
                              currentContactType: landlordContactType.BOOKING_TEAM,
                              currentContact: null,
                            });
                          }
                        }}
                      >
                        <PlusOutlined className="reference-and-contact__add-icon" />
                        {t(`cms.reference_and_contact.add_booking_team_contact.button`)}
                      </span>
                    </p>
                  </div>
                </When>
                <When condition={onlyViewPropertyRole()}>
                  <></>
                </When>
                <Otherwise>
                  <div
                    className="reference-and-contact__add-contact"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!onlyViewPropertyRole()) {
                        this.setState({
                          isShowEditContactModal: true,
                          modalType: 'create',
                          currentContactType: landlordContactType.BOOKING_TEAM,
                          currentContact: null,
                        });
                      }
                    }}
                  >
                    <PlusOutlined className="reference-and-contact__add-icon" />
                    {t(`cms.reference_and_contact.add_booking_team_contact.button`)}
                  </div>
                </Otherwise>
              </Choose>

              <If condition={property.bookingTeamContacts.length}>
                <div className="reference-and-contact__contact-section">
                  <div className="reference-and-contact__contact-section__list">
                    {property.bookingTeamContacts.map((contact, index) => (
                      <NewContactItem
                        onlyRead={false}
                        contact={contact}
                        index={index + 1}
                        contactApplyLevel={landlordContactType.PROPERTY}
                        contactType={landlordContactType.BOOKING_TEAM}
                        handleEdit={() => {
                          this.setState({
                            isShowEditContactModal: true,
                            modalType: 'edit',
                            currentContactType: landlordContactType.BOOKING_TEAM,
                            currentContact: contact,
                          });
                        }}
                        handleDelete={() => {
                          this.handleDeleteBookingTeamContact(
                            property.bookingTeamContacts.filter(c => c.id !== contact.id),
                          );
                        }}
                      />
                    ))}
                  </div>
                </div>
              </If>

              <If
                condition={
                  !property.bookingTeamContacts.length &&
                  property.landlordBookingTeamContacts.length
                }
              >
                <div className="reference-and-contact__contact-section">
                  <div className="reference-and-contact__contact-section__list">
                    {property.landlordBookingTeamContacts.map((contact, index) => (
                      <NewContactItem
                        onlyRead
                        contact={contact}
                        index={(property.bookingTeamContacts?.length || 0) + index + 1}
                        contactApplyLevel={landlordContactType.LANDLORD}
                        contactType={landlordContactType.BOOKING_TEAM}
                      />
                    ))}
                  </div>
                  <p className="reference-and-contact__contact-section__tip">
                    <Icon type="info-circle" />
                    {t('cms.reference_and_contact.cant_edit.tip')}.
                    <Link
                      to={generatePath(
                        'landlord',
                        { landlordSlug: property?.landlord?.slug },
                        {
                          tab: landlordDetailTabs.CONTACT,
                          contactType: landlordContactType.BOOKING_TEAM,
                        },
                      )}
                    >
                      <span className="reference-and-contact__link">
                        {t('cms.reference_and_contact.cant_edit.link')}
                      </span>
                    </Link>
                  </p>
                </div>
              </If>
            </Tabs.TabPane>
            {/* Finance Team Contact Tab */}
            <Tabs.TabPane
              tab={
                <Tooltip title="Servicing the finance team to liaise with properties for reconciliation, sending invoices and reminding payments, etc.">
                  {t('cms.reference_and_contact.finance_team_contact.tab')}
                </Tooltip>
              }
              key="3"
            >
              <Choose>
                <When
                  condition={
                    !property.financeTeamContacts?.length &&
                    !property.landlordFinanceTeamContacts?.length
                  }
                >
                  <div className="reference-and-contact__section__blank">
                    <p>
                      {t('cms.reference_and_contact.booking_team_contact_section.blank.tip')}.
                      <span
                        onClick={() => {
                          if (!onlyViewPropertyRole()) {
                            this.setState({
                              isShowEditContactModal: true,
                              modalType: 'create',
                              currentContactType: landlordContactType.FINANCE_TEAM,
                              currentContact: null,
                            });
                          }
                        }}
                      >
                        <PlusOutlined className="reference-and-contact__add-icon" />
                        {t(`cms.reference_and_contact.add_finance_team_contact.button`)}
                      </span>
                    </p>
                  </div>
                </When>
                <When condition={onlyViewPropertyRole()}>
                  <></>
                </When>
                <Otherwise>
                  <div
                    className="reference-and-contact__add-contact"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!onlyViewPropertyRole()) {
                        this.setState({
                          isShowEditContactModal: true,
                          modalType: 'create',
                          currentContactType: landlordContactType.FINANCE_TEAM,
                          currentContact: null,
                        });
                      }
                    }}
                  >
                    <PlusOutlined className="reference-and-contact__add-icon" />
                    {t(`cms.reference_and_contact.add_finance_team_contact.button`)}
                  </div>
                </Otherwise>
              </Choose>

              <If condition={property.financeTeamContacts?.length}>
                <div className="reference-and-contact__contact-section">
                  <h3 className="reference-and-contact__contact-section__title">
                    {t('cms.reference_and_contact.contact_section.level.property')}
                  </h3>
                  <div className="reference-and-contact__contact-section__list">
                    {property.financeTeamContacts.map((contact, index) => (
                      <NewContactItem
                        onlyRead={false}
                        contact={contact}
                        index={index + 1}
                        contactApplyLevel={landlordContactType.PROPERTY}
                        contactType={landlordContactType.FINANCE_TEAM}
                        handleEdit={() => {
                          this.setState({
                            isShowEditContactModal: true,
                            modalType: 'edit',
                            currentContactType: landlordContactType.FINANCE_TEAM,
                            currentContact: contact,
                          });
                        }}
                        handleDelete={() => {
                          this.handleDeletePropertyContact(
                            this.getPropertyContactWithType(landlordContactType.FINANCE_TEAM)
                              .filter(c => c.id !== contact.id)
                              .concat(
                                this.getPropertyContactWithType(landlordContactType.PROPERTY),
                              ),
                          );
                        }}
                      />
                    ))}
                  </div>
                </div>
              </If>

              <If condition={property.landlordFinanceTeamContacts?.length}>
                <div className="reference-and-contact__contact-section">
                  <h3 className="reference-and-contact__contact-section__title">
                    {t('cms.reference_and_contact.contact_section.level.landlord')}
                  </h3>
                  <div className="reference-and-contact__contact-section__list">
                    {property.landlordFinanceTeamContacts.map((contact, index) => (
                      <NewContactItem
                        onlyRead
                        contact={contact}
                        index={(property.financeTeamContacts?.length || 0) + index + 1}
                        contactApplyLevel={landlordContactType.LANDLORD}
                        contactType={landlordContactType.FINANCE_TEAM}
                      />
                    ))}
                  </div>
                  <p className="reference-and-contact__contact-section__tip">
                    <Icon type="info-circle" />
                    {t('cms.reference_and_contact.cant_edit.tip')}.
                    <Link
                      to={generatePath(
                        'landlord',
                        { landlordSlug: property?.landlord?.slug },
                        {
                          tab: landlordDetailTabs.CONTACT,
                          contactType: landlordContactType.FINANCE_TEAM,
                        },
                      )}
                    >
                      <span className="reference-and-contact__link">
                        {t('cms.reference_and_contact.cant_edit.link')}
                      </span>
                    </Link>
                  </p>
                </div>
              </If>
            </Tabs.TabPane>
          </Tabs>
        </div>
        <If condition={this.state.isShowEditContactModal}>
          <PropertyContactModal
            activeModal
            t={t}
            type={modalType}
            contactType={currentContactType}
            currentContact={currentContact}
            handleCloseModal={this.handleCloseEditContactModal}
            handleOnSuccess={this.props.getLatestReferenceAndContact}
            property={property}
            otherContacts={this.currentSelectedContacts()}
          />
        </If>
      </Form>
    );
  }
}

Content.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.object, // eslint-disable-line react/require-default-props
  property: PropTypes.object.isRequired,
  onRef: PropTypes.func,
  onChangeIsFetching: PropTypes.func,
  accountOwners: PropTypes.array,
  getAccountOwners: PropTypes.func,
  currentRoleSlug: PropTypes.string,
  isGettingAccountOwners: PropTypes.bool,
  getLatestReferenceAndContact: PropTypes.func,
};

Content.defaultProps = {
  t: () => {},
  onRef: () => {},
  onChangeIsFetching: () => {},
  accountOwners: [],
  getAccountOwners: () => {},
  currentRoleSlug: '',
  isGettingAccountOwners: false,
  getLatestReferenceAndContact: () => {},
};
